'use client';

import { DeleteOutlined, Edit as EditIcon } from '@mui/icons-material';
import { Button, Dialog, Grid } from '@mui/material';
import { DataGridPremium, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import React, { useCallback, useContext, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { SetShowSnackbar } from '../../../types';
import { dayjs, isAdminDashboard, isTenantPortal } from '../../../utils';
import { EditSublotHistoryModal } from './editSublotHistoryModal';
import { getHistoryErrorMessage } from './shared';

type SublotHistoryProps = {
	parkerId: string;
	terminationStatus: string;
	billingDay: number | null;
	parkerLoaded: boolean;
	sublots: { id: number; name: string }[];
	parkerHasBeenInvoiced: (date: string) => boolean;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	isEditMode: boolean;
	refetchParkersFromCache?: () => void;
	sublotHistory: any[];
	fetchParkerSublotHistory: () => void;
	sublotHistoryLoading: boolean;
	deleteParkerSublotHistory: (args) => void;
	deleteInProgress: boolean;
	updateParkerSublotHistory: (args) => void;
	updateInProgress: boolean;
};

export const SublotHistoryTable = ({
	parkerId,
	terminationStatus,
	billingDay,
	parkerLoaded,
	sublots,
	parkerHasBeenInvoiced,
	setShowSnackbar,
	tenantSubscriptionId,
	isEditMode,
	refetchParkersFromCache,
	sublotHistory,
	fetchParkerSublotHistory,
	sublotHistoryLoading,
	deleteParkerSublotHistory,
	deleteInProgress,
	updateParkerSublotHistory,
	updateInProgress,
}: SublotHistoryProps) => {
	const { hasComponentAccess } = useContext(CommonContext);
	const [showEditSublotHistoryModal, setShowEditSublotHistoryModal] = useState<boolean>(false);
	const [sublotHistoryIdToEdit, setSublotHistoryIdToEdit] = useState(null);

	const parkerPendingOrNotInvoiced = ['NOT_INVOICED', 'PENDING'].includes(terminationStatus);
	const parkerTerminated = terminationStatus === 'TERMINATED';

	const handleChangeSublotClick = useCallback(() => {
		setShowEditSublotHistoryModal(true);
	}, []);

	const handleEditClick = useCallback((sublotHistoryId: number) => {
		setSublotHistoryIdToEdit(sublotHistoryId);
		setShowEditSublotHistoryModal(true);
	}, []);

	const handleDeleteClick = useCallback(
		async (parkerSublotHistoryId: number) => {
			try {
				deleteParkerSublotHistory({
					variables: { parkerSublotHistoryId, tenantSubscriptionId },
					context: { apiVersion: 'v2' },
				});

				fetchParkerSublotHistory();
			} catch (err) {
				setShowSnackbar({
					message: getHistoryErrorMessage(err, 'Failed to delete scheduled sublot change'),
					severity: 'error',
					autoHideDuration: 6000,
				});
			}
		},
		[deleteParkerSublotHistory, fetchParkerSublotHistory, setShowSnackbar, tenantSubscriptionId],
	);

	const closeModal = useCallback(async () => {
		setShowEditSublotHistoryModal(false);
		setSublotHistoryIdToEdit(null);

		fetchParkerSublotHistory();
	}, [fetchParkerSublotHistory]);

	const displayActionIcon = useCallback(
		(row, isDelete = false) => {
			if (isDelete && row.id === sublotHistory[0]?.id) return false; // Cannot delete first record
			return !parkerHasBeenInvoiced(row.startDate);
		},
		[parkerHasBeenInvoiced, sublotHistory],
	);

	const columns: GridColDef[] = [
		{
			field: 'sublotName',
			headerName: 'Sublot',
			headerAlign: 'center' as const,
			align: 'center' as const,
			flex: 3,
			resizable: false,
			sortable: false,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			headerAlign: 'center' as const,
			align: 'center' as const,
			renderCell: (params) => dayjs.utc(params.row.startDate).format('MM/DD/YYYY'),
			flex: 2,
			resizable: false,
			sortable: false,
		},
		{
			field: 'actions',
			type: 'actions' as const,
			getActions: (params) => {
				const sublotHistoryId = params.row.id;

				return [
					...(displayActionIcon(params.row) && !isEditMode && !isTenantPortal
						? [
								<GridActionsCellItem
									key={`edit-${sublotHistoryId}`}
									icon={<EditIcon />}
									label='Edit'
									onClick={() => handleEditClick(sublotHistoryId)}
								/>,
							]
						: []),
					...(displayActionIcon(params.row, true) && !isEditMode && !isTenantPortal
						? [
								<GridActionsCellItem
									key={`delete-${sublotHistoryId}`}
									icon={<DeleteOutlined />}
									label='Delete'
									onClick={() => handleDeleteClick(sublotHistoryId)}
								/>,
							]
						: []),
				];
			},
			flex: 1,
			resizable: false,
			sortable: false,
		},
	];

	const editSublotHistoryDialog = (
		<Dialog open={showEditSublotHistoryModal}>
			<Grid item p={3}>
				<EditSublotHistoryModal
					parkerId={parkerId}
					sublotHistoryId={sublotHistoryIdToEdit}
					sublotHistory={sublotHistory}
					sublots={sublots}
					parkerHasBeenInvoiced={parkerHasBeenInvoiced}
					closeModal={closeModal}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					refetchParkersFromCache={refetchParkersFromCache}
					fetchParkerSublotHistory={fetchParkerSublotHistory}
					updateParkerSublotHistory={updateParkerSublotHistory}
					updateInProgress={updateInProgress}
				/>
			</Grid>
		</Dialog>
	);

	const loading = sublotHistoryLoading || !billingDay || deleteInProgress || !parkerLoaded;

	return showEditSublotHistoryModal ? (
		editSublotHistoryDialog
	) : (
		<>
			<DataGridPremium
				rows={sublotHistory}
				columns={columns}
				loading={loading}
				autoHeight
				disableColumnReorder
				disableColumnMenu
				hideFooter
			/>
			{(!isAdminDashboard || hasComponentAccess('ParkerDetailModalScheduleSublotChangeButton')) && (
				<Grid item pt={2}>
					{!isEditMode && isAdminDashboard ? (
						<Button
							variant='contained'
							onClick={handleChangeSublotClick}
							size='small'
							disabled={parkerPendingOrNotInvoiced || parkerTerminated}
							data-cy='schedule-sublot-change-button'
						>
							Schedule Sublot Change
						</Button>
					) : null}
				</Grid>
			)}
		</>
	);
};
